import { CriteriaNumberRange } from '../../types/cmsProduct';
import { extractGiftRange, formatNumberWithSeparator } from '../../utils/helpers';
import { tr } from '../../utils/translations/tr';

export type FormatRangeTitleStaticText = {
  defaultGiftAmountLabel: string;
  defaultGiftAmountSuffix: string;
  defaultGiftTenureLabel: string;
  defaultGiftTenureWithEndSuffix: string;
  defaultGiftTenureWithOutEndSuffix: string;
  reward_modal_loan_amount_with_end: string;
  reward_modal_loan_amount_without_end: string;
  reward_modal_tenure_with_end: string;
  reward_modal_tenure_without_end: string;
};

// Pure utility (no React). Lives in its own module so consumers that only need
// the formatter don't pull in the heavy modal component via static analysis.
// The lazy facade in RewardAsideModal.tsx re-exports from here.
export const formatRangeTitle = (
  currency: string,
  criteriaRange: CriteriaNumberRange[] | undefined,
  staticText: FormatRangeTitleStaticText,
) => {
  if (!criteriaRange) {
    return;
  }

  const {
    start: giftAmountStart,
    end: giftAmountEnd,
    suffix: giftAmountSuffix,
    label: giftAmountLabel,
    prefix: giftAmountPrefix,
  } = extractGiftRange(criteriaRange, 'ELIGIBLE_LOAN_RANGE');
  const {
    start: giftTenureStart,
    end: giftTenureEnd,
    suffix: giftTenureSuffix,
    label: giftTenureLabel,
    prefix: giftTenurePrefix,
  } = extractGiftRange(criteriaRange, 'ELIGIBLE_TENURE_RANGE');

  if (!giftAmountStart) {
    return;
  }

  const amountPrefix = giftAmountPrefix || currency;
  const defaultGiftAmountLabel = staticText.defaultGiftAmountLabel;
  const defaultGiftAmountSuffix = staticText.defaultGiftAmountSuffix ?? '';

  let rangeTitle = giftAmountEnd
    ? tr('reward_modal_loan_amount_with_end', staticText, {
        start: `${amountPrefix}${formatNumberWithSeparator({ value: giftAmountStart, thousandsSeparator: ',' })} ${giftAmountSuffix ?? ''}`,
        end: `${amountPrefix}${formatNumberWithSeparator({ value: giftAmountEnd, thousandsSeparator: ',' })} ${giftAmountSuffix ?? ''}`,
        label: giftAmountLabel || defaultGiftAmountLabel,
      })
    : tr('reward_modal_loan_amount_without_end', staticText, {
        start: `${amountPrefix}${formatNumberWithSeparator({ value: giftAmountStart, thousandsSeparator: ',' })} ${giftAmountSuffix || defaultGiftAmountSuffix}`,
        label: giftAmountLabel || defaultGiftAmountLabel,
      });

  if (giftTenureStart) {
    const tenureLabel = giftTenureLabel || staticText.defaultGiftTenureLabel;

    const tenureTitle = giftTenureEnd
      ? tr('reward_modal_tenure_with_end', staticText, {
          prefix: giftTenurePrefix ?? '',
          start: giftTenureStart.toString(),
          end: giftTenureEnd.toString(),
          suffix: giftTenureSuffix || staticText.defaultGiftTenureWithEndSuffix,
          label: tenureLabel,
        })
      : tr('reward_modal_tenure_without_end', staticText, {
          prefix: giftTenurePrefix ?? '',
          start: giftTenureStart.toString(),
          suffix:
            giftTenureSuffix || staticText.defaultGiftTenureWithOutEndSuffix,
          label: tenureLabel,
        }).trim();
    rangeTitle += ` (${tenureTitle})`;
  }

  return rangeTitle;
};
